import { ComponentType, lazy } from 'react';

const CHUNK_RELOAD_KEY_PREFIX = 'chunk-reload-attempted:';
const CHUNK_LOAD_ERROR_PATTERN = /Failed to fetch dynamically imported module|Importing a module script failed|text\/html.+valid JavaScript MIME type|Unable to preload CSS/i;
const CHUNK_RELOAD_QUERY_PARAM = '__chunk_reload';
const MAX_CHUNK_RELOAD_ATTEMPTS = 2;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_LOAD_ERROR_PATTERN.test(message);
}

function getAttemptCount(storageKey: string): number {
  if (typeof window === 'undefined') {
    return 0;
  }

  let rawValue: string | null = null;
  try {
    rawValue = window.sessionStorage.getItem(storageKey);
  } catch {
    rawValue = null;
  }
  const parsedValue = rawValue ? Number.parseInt(rawValue, 10) : 0;

  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function setAttemptCount(storageKey: string, attemptCount: number): boolean {
  try {
    window.sessionStorage.setItem(storageKey, String(attemptCount));
    return true;
  } catch {
    return false;
  }
}

function clearAttemptCount(storageKey: string) {
  try {
    window.sessionStorage.removeItem(storageKey);
  } catch {
    // Ignore storage failures; chunk recovery must not mask the original load result.
  }
}

function replaceWithCacheBustedUrl() {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set(CHUNK_RELOAD_QUERY_PARAM, Date.now().toString());
  window.location.replace(currentUrl.toString());
}

export function clearChunkReloadQueryParam() {
  if (typeof window === 'undefined') {
    return;
  }

  const currentUrl = new URL(window.location.href);
  if (!currentUrl.searchParams.has(CHUNK_RELOAD_QUERY_PARAM)) {
    return;
  }

  currentUrl.searchParams.delete(CHUNK_RELOAD_QUERY_PARAM);
  window.history.replaceState(window.history.state, '', currentUrl.toString());
}

export function attemptChunkRecovery(key: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const storageKey = `${CHUNK_RELOAD_KEY_PREFIX}${key}`;
  const attemptCount = getAttemptCount(storageKey);

  if (attemptCount >= MAX_CHUNK_RELOAD_ATTEMPTS) {
    clearAttemptCount(storageKey);
    return false;
  }

  const storedAttempt = setAttemptCount(storageKey, attemptCount + 1);
  if (!storedAttempt) {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has(CHUNK_RELOAD_QUERY_PARAM)) {
      return false;
    }
  }
  replaceWithCacheBustedUrl();
  return true;
}

export async function loadWithChunkReload<T>(
  loader: () => Promise<T>,
  key: string
): Promise<T> {
  const storageKey = `${CHUNK_RELOAD_KEY_PREFIX}${key}`;

  try {
    const loaded = await loader();

    if (typeof window !== 'undefined') {
      clearAttemptCount(storageKey);
      clearChunkReloadQueryParam();
    }

    return loaded;
  } catch (error) {
    if (isChunkLoadError(error) && attemptChunkRecovery(key)) {
      return new Promise<T>(() => undefined);
    }

    if (typeof window !== 'undefined') {
      clearAttemptCount(storageKey);
    }

    throw error;
  }
}

// Dynamic imports need to accept components with arbitrary prop types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithChunkReload<T extends { default: ComponentType<any> }>(
  loader: () => Promise<T>,
  key: string
) {
  return lazy(() => loadWithChunkReload(loader, key));
}
